import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiZap, FiPlus, FiEdit2, FiTrash2, FiSearch, FiStar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { apiGet, apiDelete } from '@/lib/api';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { toast } from 'sonner';
import { IntegrationFormDrawer } from '../components/IntegrationFormDrawer';
import {
  AppIntegration,
  IntegrationsListResponse,
  IntegrationStatus,
  STATUS_LABELS,
  STATUS_VARIANTS,
} from '../types';

export const IntegrationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-integrations', page, search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      return apiGet<IntegrationsListResponse>(`/integrations?${params}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/integrations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      toast.success('Integracao removida');
    },
    onError: () => toast.error('Erro ao remover integracao'),
  });

  const integrations = data?.items || [];
  const pagination = data ? { page: data.page, limit: data.limit, total: data.total, totalPages: data.totalPages } : undefined;

  const openCreate = () => {
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEdit = (id: string) => {
    setEditingId(id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
  };

  const handleDelete = (integration: AppIntegration) => {
    if (integration.isDefault) {
      toast.error('Nao e possivel excluir uma integracao padrao');
      return;
    }
    if (confirm(`Deseja remover a integracao "${integration.name}"?`)) {
      deleteMutation.mutate(integration.id);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Integracoes</h1>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Gerencie as integracoes do app</p>
        </div>
        <Button icon={FiPlus} onClick={openCreate}>
          Nova integracao
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 max-w-sm">
          <Input
            icon={FiSearch}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nome..."
          />
        </div>
        <div className="w-40">
          <Select
            value={statusFilter}
            onChange={(value) => { setStatusFilter(value); setPage(1); }}
            options={[
              { value: '', label: 'Todos os status' },
              { value: 'active', label: 'Ativo' },
              { value: 'inactive', label: 'Inativo' },
              { value: 'error', label: 'Erro' },
              { value: 'pending', label: 'Pendente' },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </div>
          ) : integrations.length === 0 ? (
            <div className="flex flex-col items-center text-center p-12">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: 'var(--theme-primary-background)' }}
              >
                <FiZap className="h-6 w-6" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--theme-card-text)' }}>
                Nenhuma integracao encontrada
              </h3>
              <p className="text-sm max-w-sm" style={{ color: 'var(--theme-muted-text)' }}>
                {search || statusFilter
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Crie a primeira integracao para o seu app.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    {['Provider', 'Nome', 'Status', 'Padrao', 'Capabilities', 'Criado em', 'Acoes'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {integrations.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`cursor-pointer transition-colors ${index > 0 ? 'border-t' : ''}`}
                      style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                      onClick={() => openEdit(item.id)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                        <span className="font-medium">{item.providerName}</span>
                        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{item.providerId}</p>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                        {item.name}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_VARIANTS[item.status as IntegrationStatus] || 'default'}>
                          {STATUS_LABELS[item.status as IntegrationStatus] || item.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {item.isDefault && (
                          <FiStar className="h-4 w-4" style={{ color: '#f59e0b' }} />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {item.capabilities.slice(0, 3).map((cap) => (
                            <span
                              key={cap}
                              className="text-[10px] px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}
                            >
                              {cap.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {item.capabilities.length > 3 && (
                            <span className="text-[10px] px-1.5 py-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                              +{item.capabilities.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openEdit(item.id)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: 'var(--theme-muted-text)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            title="Editar"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: 'var(--theme-muted-text)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                              e.currentTarget.style.color = 'var(--theme-error-color)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'var(--theme-muted-text)';
                            }}
                            title="Remover"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div
              className="flex items-center justify-between px-4 py-3 border-t"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {pagination.total} integracao{pagination.total !== 1 ? 'es' : ''} encontrada{pagination.total !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  <FiChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs px-2" style={{ color: 'var(--theme-card-text)' }}>
                  {page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drawer */}
      <IntegrationFormDrawer
        integrationId={editingId}
        isOpen={drawerOpen}
        onClose={closeDrawer}
      />
    </div>
  );
};
